/*
  Admin page — pure rules (no React), shared by the six Admin tabs and their tests.
*/
import { DEFAULT_SHOP_DRAWING_DISCIPLINES, normalizeDisciplineToken, THEME_OPTIONS } from '@/contexts/AppSettingsContext';
import { EDITABLE_PAGES, PERMISSION_MODULES, SUPER_ADMIN_ROLE } from '@/lib/permissions';
import { formatDate, formatDateTime, formatTime, formattingDefaults, toDateInputValue } from '@/lib/format';
import { storageKey } from '@/lib/storage';

/* ----------------------------------------------------------------------------------------------
   Tabs — the active tab lives in the URL (?tab=integrations) so refresh and shared links keep it
---------------------------------------------------------------------------------------------- */
export const ADMIN_TAB_VALUES = ['settings', 'users', 'access', 'stages', 'history', 'integrations'];
export const DEFAULT_ADMIN_TAB = 'users';

export const parseAdminTab = (value) => {
  const clean = String(value || '').trim().toLowerCase();
  return ADMIN_TAB_VALUES.includes(clean) ? clean : DEFAULT_ADMIN_TAB;
};

/* ----------------------------------------------------------------------------------------------
   People
---------------------------------------------------------------------------------------------- */
export const ROLE_FAMILIES = [
  { key: 'admins', label: 'Admins', roles: [SUPER_ADMIN_ROLE, 'Admin'], note: 'Super Admin + Admin — can add, delete and re-role people' },
  { key: 'site', label: 'Site & engineering', roles: ['Site Engineer', 'Senior Site Engineer', 'Site Supervisor', 'Technician'] },
  { key: 'office', label: 'Office', roles: ['PM', 'Presales', 'Accountant', 'Storekeeper', 'Backoffice Engineer', 'Viewer'] },
];

export const roleFamily = (role) => (
  ROLE_FAMILIES.find((family) => family.roles.includes(String(role || '').trim()))?.key || 'office'
);

export const isAdminRole = (role) => roleFamily(role) === 'admins';

const TEST_ACCOUNT_NAMES = new Set(['test', 'tester', 'testing', 'app', 'demo', 'dummy', 'temp', 'tmp', 'sample']);

/**
 * Accounts that look like test / service logins: the name or the address before @ is a throwaway word
 * ('test', 'app', 'demo'…), or the address has no real domain ('test@test').
 */
export const isTestAccount = (user = {}) => {
  const name = String(user.name || '').trim().toLowerCase();
  const email = String(user.email || '').trim().toLowerCase();
  const [local = '', domain] = email.split('@');
  if (TEST_ACCOUNT_NAMES.has(name) || TEST_ACCOUNT_NAMES.has(local)) return true;
  return Boolean(domain !== undefined && domain && !domain.includes('.'));
};

/** Super Admin's login is the username 'admin' — show it as a username, not a broken e-mail. */
export const userLoginLabel = (user = {}) => {
  const email = String(user.email || '').trim();
  if (!email) return '';
  return email.includes('@') ? email : `${email} (username)`;
};

export const userInitials = (name) => {
  const words = String(name || '?').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return `${words[0][0]}${words[words.length - 1][0]}`.toUpperCase();
};

const AVATAR_FILLS = ['#3d3da3', '#0f766e', '#b45309', '#be185d', '#4d7c0f', '#6d28d9'];
export const avatarFill = (key) => {
  const text = String(key || '');
  let hash = 0;
  for (let index = 0; index < text.length; index += 1) hash = (hash * 31 + text.charCodeAt(index)) >>> 0;
  return AVATAR_FILLS[hash % AVATAR_FILLS.length];
};

const byCreated = (a, b) => String(a.created_at || '').localeCompare(String(b.created_at || ''));

export const userStats = (users = []) => {
  const list = Array.isArray(users) ? users : [];
  const testAdmins = list.filter((user) => isAdminRole(user.role) && isTestAccount(user));
  return {
    all: list.length,
    admins: list.filter((user) => roleFamily(user.role) === 'admins').length,
    site: list.filter((user) => roleFamily(user.role) === 'site').length,
    office: list.filter((user) => roleFamily(user.role) === 'office').length,
    neverChangedPassword: list.filter((user) => !user.password_changed_at).length,
    testAdmins,
  };
};

/** Search (name / e-mail), family segment ('all' | 'admins' | 'site' | 'office') and the password chip. */
export const filterUsers = (users = [], { search = '', family = 'all', passwordNever = false } = {}) => {
  const needle = String(search || '').trim().toLowerCase();
  return (Array.isArray(users) ? users : []).filter((user) => {
    if (family !== 'all' && roleFamily(user.role) !== family) return false;
    if (passwordNever && user.password_changed_at) return false;
    if (!needle) return true;
    return [user.name, user.email, user.role].some((value) => String(value || '').toLowerCase().includes(needle));
  });
};

/** Group rows by role family (Admins → Site & engineering → Office), oldest account first inside each. */
export const groupUsers = (users = []) => ROLE_FAMILIES
  .map((family) => {
    const members = users.filter((user) => roleFamily(user.role) === family.key).sort(byCreated);
    const rolesPresent = family.roles.filter((role) => members.some((user) => user.role === role));
    return {
      key: family.key,
      label: family.label,
      note: family.note || rolesPresent.join(', '),
      users: members,
    };
  })
  .filter((group) => group.users.length);

/* ----------------------------------------------------------------------------------------------
   Settings
---------------------------------------------------------------------------------------------- */
export const themeLabel = (value) => THEME_OPTIONS.find((option) => option.value === value)?.label || value || '—';

/** Disciplines from the built-in list that are not active yet (offered as one-click chips). */
export const suggestedDisciplines = (active = []) => DEFAULT_SHOP_DRAWING_DISCIPLINES
  .filter((code) => code !== 'OTHER' && !active.includes(code));

/** What the typed text will be saved as, or '' when nothing usable was typed ('OTHER' is always active). */
export const disciplinePreview = (value) => normalizeDisciplineToken(value);

/** One line per unsaved difference between the saved settings and the draft (`options`: workspace choices). */
export const settingsChanges = (saved = {}, draft = {}, options = null) => {
  const changes = [];
  const savedName = String(saved.app_name || '').trim();
  const draftName = String(draft.app_name || '').trim();
  if (savedName !== draftName) changes.push({ key: 'app_name', text: `App name: ${savedName || '—'} → ${draftName || '—'}` });
  if ((saved.theme || '') !== (draft.theme || '')) {
    changes.push({ key: 'theme', text: `Theme: ${themeLabel(saved.theme)} → ${themeLabel(draft.theme)}` });
  }
  const before = saved.shop_drawing_disciplines || [];
  const after = draft.shop_drawing_disciplines || [];
  const added = after.filter((code) => !before.includes(code));
  const removed = before.filter((code) => !after.includes(code));
  if (added.length || removed.length) {
    const parts = [...added.map((code) => `+${code}`), ...removed.map((code) => `−${code}`)];
    changes.push({ key: 'disciplines', text: `Disciplines: ${parts.join(', ')}` });
  }
  // 26 #8 — workspace defaults (only when both sides carry them)
  if (saved.workspace && draft.workspace) {
    WORKSPACE_FIELDS.forEach((field) => {
      const from = saved.workspace[field];
      const to = draft.workspace[field];
      if (from === to) return;
      changes.push({
        key: `workspace-${field}`,
        text: `${WORKSPACE_FIELD_LABELS[field]}: ${workspaceChoiceLabel(options, field, from)} → ${workspaceChoiceLabel(options, field, to)}`,
      });
    });
  }
  return changes;
};

/* ----------------------------------------------------------------------------------------------
   Workspace defaults (wave 3 · 26 #8) — time zone, currency, work week, date format
---------------------------------------------------------------------------------------------- */
export const WORKSPACE_FIELDS = ['time_zone', 'currency', 'work_week', 'date_format'];
export const WORKSPACE_FIELD_LABELS = {
  time_zone: 'Time zone', currency: 'Currency', work_week: 'Work week', date_format: 'Date format',
};
// The labels the page shows before /api/admin/settings answered (the server's lists are the source of truth).
export const FALLBACK_WORKSPACE_OPTIONS = {
  time_zone: [{ value: 'Asia/Dubai', label: 'Asia/Dubai · GST (UTC+4)', short: 'GST', offset_minutes: 240 }],
  currency: [{ value: 'AED', label: 'AED — UAE Dirham' }],
  work_week: [
    { value: 'mon_fri', label: 'Mon–Fri', working_days: [0, 1, 2, 3, 4] },
    { value: 'mon_sat', label: 'Mon–Sat', working_days: [0, 1, 2, 3, 4, 5] },
    { value: 'sun_thu', label: 'Sun–Thu', working_days: [6, 0, 1, 2, 3] },
  ],
  date_format: [{ value: 'd_mmm_yyyy', label: 'Day Month Year', example: '30 Sep 2026' }],
};

/** 'Asia/Dubai · GST (UTC+4)', 'Mon–Sat', '30 Sep 2026' (a date format reads as its example). */
export const workspaceChoiceLabel = (options, field, value) => {
  const list = (options && options[field]) || FALLBACK_WORKSPACE_OPTIONS[field] || [];
  const found = list.find((option) => option.value === value);
  if (!found) return value || '—';
  return field === 'date_format' ? found.example || found.label : found.label;
};

/* ----------------------------------------------------------------------------------------------
   Access
---------------------------------------------------------------------------------------------- */
export const moduleLabel = (key) => PERMISSION_MODULES.find((module) => module.key === key)?.label || key;

/** For each module: how many roles can open it (Super Admin always can). */
export const moduleAccessCounts = (roles = [], permissions = {}, modules = PERMISSION_MODULES) => {
  const counts = {};
  modules.forEach((module) => {
    const key = typeof module === 'string' ? module : module.key;
    counts[key] = roles.filter((role) => role === SUPER_ADMIN_ROLE || (permissions[role] || []).includes(key)).length;
  });
  return counts;
};

/* ----------------------------------------------------------------------------------------------
   Stage templates
---------------------------------------------------------------------------------------------- */
/** Plain-language summary of how the saved template differs from what new projects really get. */
export const stageSyncSummary = (sync) => {
  if (!sync) return null;
  const parts = [];
  if (sync.added?.length) parts.push(`${sync.added.length} new`);
  if (sync.renamed?.length) parts.push(`${sync.renamed.length} renamed`);
  if (sync.dropped?.length) parts.push(`${sync.dropped.length} dropped`);
  return {
    outOfDate: sync.in_use === false,
    approvalMismatches: sync.approval_mismatches || [],
    differences: Number(sync.differences || 0),
    breakdown: parts.join(', '),
    liveCount: (sync.live_stages || []).length,
  };
};

/**
 * What a switch really changed. create_project resets every live stage's approval to the built-in
 * pipeline, so only claim "for new projects" when nothing overrides the saved value: the template is in
 * use and the stage is not listed in sync.approval_mismatches.
 */
export function approvalToast(stageName, required, sync) {
  const setting = required ? 'approval required' : 'no approval needed';
  if (!sync) return { title: `${stageName}: ${setting}`, description: 'Saved to the template.' };
  if (sync.in_use === false) {
    return {
      title: `${stageName}: ${setting} in the template`,
      description: 'Saved to the template — new projects still use the built-in pipeline until the template is up to date.',
    };
  }
  const override = (sync.approval_mismatches || []).find((item) => item.name === stageName);
  if (override) {
    return {
      title: `${stageName}: ${setting} in the template`,
      description: `Saved to the template — new projects still use the built-in setting (${override.live ? 'approval required' : 'no approval'}).`,
    };
  }
  return { title: `${stageName}: ${setting} for new projects`, description: undefined };
}

/* ----------------------------------------------------------------------------------------------
   History
---------------------------------------------------------------------------------------------- */
export const HISTORY_AREAS = [
  { value: 'project', label: 'Project' },
  { value: 'procurement', label: 'Procurement' },
  { value: 'inventory', label: 'Inventory' },
  { value: 'shop_drawings', label: 'Shop Drawings' },
  { value: 'admin', label: 'Admin' },
];
export const HISTORY_ACTIONS = [
  { value: 'create', label: 'Create' },
  { value: 'update', label: 'Update' },
  { value: 'delete', label: 'Delete' },
  { value: 'import', label: 'Import' },
  { value: 'reorder', label: 'Reorder' },
  { value: 'restore', label: 'Restore' },
];
export const HISTORY_STATUSES = [
  { value: 'available', label: 'Restorable' },
  { value: 'undone', label: 'Undone' },
  { value: 'canceled', label: 'Locked' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'partial', label: 'Partial' },
];
export const HISTORY_DATE_RANGES = [
  { value: '7d', label: 'Last 7 days', days: 7 },
  { value: '14d', label: 'Last 14 days', days: 14 },
  { value: '30d', label: 'Last 30 days', days: 30 },
  { value: '90d', label: 'Last 90 days', days: 90 },
];
// `day` (YYYY-MM-DD, company time zone): one day only — "Show these changes" of a pattern (30 #9).
export const DEFAULT_HISTORY_FILTERS = { search: '', person: '', area: '', action: '', status: '', range: '14d', day: '' };
export const HISTORY_PAGE_SIZE = 200;

export const optionLabel = (options, value) => options.find((option) => option.value === value)?.label || value || '';
export const historyAreaLabel = (value) => {
  const found = HISTORY_AREAS.find((option) => option.value === value);
  if (found) return found.label;
  const text = String(value || '').replace(/_/g, ' ').trim();
  return text && text !== 'other' ? text.charAt(0).toUpperCase() + text.slice(1) : 'Other';
};

const pad2 = (value) => String(value).padStart(2, '0');
/** Same shape as the stored created_at ('…T20:00:00+00:00') so the API's string range compares correctly. */
const toStoredInstant = (ms) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}:${pad2(d.getUTCSeconds())}+00:00`;
};

/**
 * 'Last 14 days' = from 00:00 thirteen days before today (today counts as day 1), in the company's zone
 * (Admin › Settings › Workspace defaults) — the same instant GET /admin/users/activity and /history/insights
 * start from, so "Changes · 14 days" on Users and the History it opens count the same entries.
 */
export const historyRangeStart = (rangeValue, now = new Date(), timeZone = formattingDefaults().timeZone) => {
  const range = HISTORY_DATE_RANGES.find((option) => option.value === rangeValue);
  if (!range) return '';
  const today = toDateInputValue(now, { timeZone }); // YYYY-MM-DD in the company zone
  const [y, m, d] = today.split('-').map(Number);
  return toStoredInstant(dayStart(y, m, d - (range.days - 1), 240, timeZone)); // Date.UTC rolls month ends
};

/**
 * The text sent as `search`. The history API matches it as a regular expression (only backslashes are
 * escaped), so "LPO (" made it fail with a 500. Until the API escapes the text itself, search with the
 * longest part of what was typed between regex operators — a slightly wider match, never an error.
 * ('.' stays: it can't break the pattern and is common in e-mail addresses.)
 */
export const historySearchTerm = (value) => String(value || '')
  .split(/[()[\]{}*+?^$|]+/)
  .map((part) => part.trim())
  .filter(Boolean)
  .reduce((longest, part) => (part.length > longest.length ? part : longest), '');

/** Query params for GET /undo/history (only the set filters). */
export const historyQueryParams = (filters = DEFAULT_HISTORY_FILTERS, { now, before } = {}) => {
  const params = { limit: HISTORY_PAGE_SIZE };
  const search = historySearchTerm(filters.search);
  if (search) params.search = search;
  if (filters.person) params.user_id = filters.person;
  if (filters.area) params.page_type = filters.area;
  if (filters.action) params.action_type = filters.action;
  if (filters.status) params.status = filters.status;
  const dayRange = historyDayRange(filters.day, filters.dayOffsetMinutes, filters.dayTimeZone);
  const from = dayRange ? dayRange.from : historyRangeStart(filters.range, now);
  if (from) params.date_from = from;
  if (before) params.date_to = before;
  else if (dayRange) params.date_to = dayRange.to;
  return params;
};

/**
 * Minutes `timeZone` is ahead of UTC at `instant` (ms) — daylight saving included (Intl); null when the zone
 * is unknown to this browser.
 */
export const zoneOffsetAt = (timeZone, instant) => {
  if (!timeZone || !Number.isFinite(instant)) return null;
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(new Date(instant));
    const part = (type) => Number(parts.find((item) => item.type === type)?.value);
    const local = Date.UTC(part('year'), part('month') - 1, part('day'), part('hour') % 24, part('minute'));
    return Math.round((local - Math.floor(instant / 60000) * 60000) / 60000);
  } catch {
    return null;
  }
};

/** The instant a calendar day starts in `timeZone` (summer time followed); the fixed offset when no zone. */
const dayStart = (year, month, day, offsetMinutes, timeZone) => {
  const midnight = Date.UTC(year, month - 1, day);
  const fixed = Number(offsetMinutes || 0);
  const first = zoneOffsetAt(timeZone, midnight);
  if (first === null) return midnight - fixed * 60000;
  const second = zoneOffsetAt(timeZone, midnight - first * 60000);
  return midnight - (second ?? first) * 60000;
};

/**
 * One calendar day (YYYY-MM-DD) in the company's zone as stored instants: { from, to } (to = next midnight).
 * With `timeZone` the day's own offset is used (a summer day in Cairo starts at 21:00 UTC, a winter day at
 * 22:00); `offsetMinutes` is the fallback for a zone the browser does not know.
 */
export const historyDayRange = (day, offsetMinutes = 240, timeZone = '') => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(day || ''));
  if (!match) return null;
  const [year, month, date] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const start = dayStart(year, month, date, offsetMinutes, timeZone);
  const end = dayStart(year, month, date + 1, offsetMinutes, timeZone); // Date.UTC rolls over month ends
  return { from: toStoredInstant(start), to: toStoredInstant(end) };
};

/** Merge an older page into the loaded list (Load older repeats the boundary entry). */
export const mergeHistoryPages = (current = [], older = []) => {
  const seen = new Set(current.map((item) => item.id));
  return [...current, ...older.filter((item) => !seen.has(item.id))];
};

// Area names and placeholders are not a target ("Unknown" comes from entries without an entity).
const GENERIC_HISTORY_TARGETS = new Set(['admin', 'inventory', 'procurement', 'project', 'shop drawings', 'unknown']);
const historyTarget = (item = {}) => String(item.project_label || '').trim()
  || (item.entity_label && !GENERIC_HISTORY_TARGETS.has(String(item.entity_label).trim().toLowerCase()) ? String(item.entity_label).trim() : '');

/** Rows grouped for the feed: 'time' → per GST day, 'project' → per project / area, 'person' → per user. */
export const groupHistory = (items = [], mode = 'time') => {
  const groups = new Map();
  items.forEach((item) => {
    let key;
    let label;
    if (mode === 'person') {
      key = item.user_id || item.user_name || 'unknown';
      label = item.user_name || 'Unknown person';
    } else if (mode === 'project') {
      const target = historyTarget(item);
      key = target ? `p:${target}` : `a:${item.page_type || 'other'}`;
      label = target || `${historyAreaLabel(item.page_type)} · no project`;
    } else {
      key = toDateInputValue(item.created_at) || 'unknown';
      label = key === 'unknown' ? 'Unknown date' : formatDate(item.created_at, { weekday: true, year: 'auto' }).replace(',', '');
    }
    if (!groups.has(key)) groups.set(key, { key, label, items: [] });
    groups.get(key).items.push(item);
  });
  return [...groups.values()];
};

export const historyTargetLabel = historyTarget;

/** "All 200 restorable" or "198 restorable · 2 superseded". */
export const historyStatusSummary = (items = []) => {
  const counts = {};
  items.forEach((item) => { counts[item.status || 'available'] = (counts[item.status || 'available'] || 0) + 1; });
  const restorable = counts.available || 0;
  const others = HISTORY_STATUSES
    .filter((status) => status.value !== 'available' && counts[status.value])
    .map((status) => `${counts[status.value]} ${status.label.toLowerCase()}`);
  if (!items.length) return '';
  if (!others.length) return `All ${restorable} restorable`;
  return [`${restorable} restorable`, ...others].join(' · ');
};

/* ----------------------------------------------------------------------------------------------
   Integrations
---------------------------------------------------------------------------------------------- */
export const QUICKBOOKS_KEY_LABELS = {
  QUICKBOOKS_CLIENT_ID: 'Client ID',
  QUICKBOOKS_CLIENT_SECRET: 'Client secret',
  QUICKBOOKS_REDIRECT_URI: 'Redirect URI',
  QUICKBOOKS_DEFAULT_ITEM_ID: 'Default service item',
};
export const QUICKBOOKS_KEYS = Object.keys(QUICKBOOKS_KEY_LABELS);

export const quickBooksKeyLabel = (key) => QUICKBOOKS_KEY_LABELS[key] || String(key || '').replace(/^QUICKBOOKS_/, '').replace(/_/g, ' ').toLowerCase();

/** 'setup' (server keys missing) · 'ready' (can connect) · 'connected'. */
export const quickBooksState = (status) => {
  if (status?.connected) return 'connected';
  if (status && status.configured === false) return 'setup';
  return 'ready';
};

/** Ready-to-paste message for IT with the exact server keys to add. */
export const quickBooksSetupNote = ({ missing = [], appName = 'Sara', origin = '' } = {}) => {
  const keys = missing.length ? missing : QUICKBOOKS_KEYS;
  const lines = [
    `Hi — please set up the QuickBooks Online connection for ${appName}.`,
    '',
    `Add these ${keys.length} environment variable${keys.length === 1 ? '' : 's'} to the backend server, then restart it:`,
    ...keys.map((key) => `• ${key} — ${quickBooksKeyLabel(key)}`),
    '',
    'Client ID / secret come from the Intuit Developer app (Keys & credentials).',
  ];
  if (origin) lines.push(`The redirect URI must point at this server's /api/quickbooks/callback (app: ${origin}).`);
  lines.push('Once added, an admin can connect from Admin › Integrations.');
  return lines.join('\n');
};

/* ==============================================================================================
   Wave 2
============================================================================================== */

/* ----------------------------------------------------------------------------------------------
   People (27 #1 role popover · #4 account health · #6 reset password · #7 add user)
---------------------------------------------------------------------------------------------- */
/** The same rules as the backend (security_utils.validate_password). */
export const PASSWORD_RULES = [
  { key: 'length', label: '12 or more characters', test: (value) => value.length >= 12 && value.length <= 128 },
  { key: 'lower', label: 'A lowercase letter', test: (value) => /[a-z]/.test(value) },
  { key: 'upper', label: 'An uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { key: 'digit', label: 'A number', test: (value) => /\d/.test(value) },
];

export const passwordChecks = (value) => {
  const text = String(value || '');
  return PASSWORD_RULES.map((rule) => ({ key: rule.key, label: rule.label, ok: rule.test(text) }));
};

export const passwordIsValid = (value) => passwordChecks(value).every((check) => check.ok);

// No look-alikes (l / 1 / I / O / 0) — the password is often read out or typed from a phone.
const PASSWORD_SETS = ['abcdefghijkmnpqrstuvwxyz', 'ABCDEFGHJKLMNPQRSTUVWXYZ', '23456789', '-_.!#'];

const randomIndex = (max, random) => {
  if (random) return Math.floor(random() * max) % max;
  const cryptoApi = typeof window !== 'undefined' ? window.crypto : undefined;
  if (cryptoApi?.getRandomValues) {
    const buffer = new Uint32Array(1);
    cryptoApi.getRandomValues(buffer);
    return buffer[0] % max;
  }
  return Math.floor(Math.random() * max);
};

/** A strong password that always passes PASSWORD_RULES (one of each set, then shuffled). */
export const generatePassword = (length = 16, random) => {
  const size = Math.max(12, Math.min(64, length));
  const all = PASSWORD_SETS.join('');
  const chars = PASSWORD_SETS.map((set) => set[randomIndex(set.length, random)]);
  while (chars.length < size) chars.push(all[randomIndex(all.length, random)]);
  for (let index = chars.length - 1; index > 0; index -= 1) {
    const swap = randomIndex(index + 1, random);
    [chars[index], chars[swap]] = [chars[swap], chars[index]];
  }
  return chars.join('');
};

/** The company e-mail domain: the most common one among real (non-test) accounts. */
export const companyEmailDomain = (users = []) => {
  const counts = new Map();
  (Array.isArray(users) ? users : []).forEach((user) => {
    if (isTestAccount(user)) return;
    const domain = String(user.email || '').trim().toLowerCase().split('@')[1];
    if (domain && domain.includes('.')) counts.set(domain, (counts.get(domain) || 0) + 1);
  });
  return [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))[0]?.[0] || '';
};

/** 'ali' + 'saraeengg.com' → 'ali@saraeengg.com'; '' when the text already is an address. */
export const suggestedEmail = (value, domain) => {
  const text = String(value || '').trim().toLowerCase();
  if (!text || !domain || text.includes('@')) return '';
  const local = text.replace(/\s+/g, '.').replace(/[^a-z0-9._-]/g, '');
  return local ? `${local}@${domain}` : '';
};

export const looksLikeEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());

const shortList = (items, max = 3) => (items.length <= max ? items : [...items.slice(0, max), `+${items.length - max} more`]);
const joinWords = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`);

/**
 * What a role can open, in one line: "13 of 18 modules · no Invoicing, Admin, AI +2 more".
 * Super Admin: everything, always.
 */
export const roleAccessSummary = (role, permissions = {}, modules = PERMISSION_MODULES) => {
  const keys = modules.map((module) => (typeof module === 'string' ? module : module.key));
  if (role === SUPER_ADMIN_ROLE) {
    return { count: keys.length, total: keys.length, missing: [], text: `All ${keys.length} modules — always` };
  }
  const granted = new Set(permissions[role] || []);
  const missing = keys.filter((key) => !granted.has(key)).map(moduleLabel);
  const count = keys.length - missing.length;
  const listed = missing.slice(0, 3).join(', ') + (missing.length > 3 ? ` +${missing.length - 3} more` : '');
  const text = missing.length ? `${count} of ${keys.length} modules · no ${listed}` : `All ${keys.length} modules`;
  return { count, total: keys.length, missing, text };
};

/** 'promote' (gains Admin rights) · 'demote' (loses them) · 'admin' (between Admin roles) · 'plain'. */
export const roleChangeKind = (from, to) => {
  const before = isAdminRole(from);
  const after = isAdminRole(to);
  if (!before && after) return 'promote';
  if (before && !after) return 'demote';
  if (before && after && from !== to) return 'admin';
  return 'plain';
};

/** Roles this viewer may give `target` — the backend's rules: only Super Admin changes an Admin's role
 *  or hands out Super Admin; Admins may re-role everyone else (Admin included). */
export const assignableRoles = (roles = [], { viewerIsSuperAdmin = false, target } = {}) => {
  if (viewerIsSuperAdmin) return roles;
  if (target && isAdminRole(target.role)) return [];
  return roles.filter((role) => role !== SUPER_ADMIN_ROLE);
};

/** Admin-rights health: test accounts with Admin, people who never changed their password. */
/** This browser's "Dismiss" of the account-health callout: the signature (test-admin ids) it was dismissed for. */
export const HEALTH_DISMISS_KEY = storageKey('admin', 'users', 'health-dismissed');

export const accountHealth = (users = []) => {
  const stats = userStats(users);
  const signature = stats.testAdmins.map((user) => user.id).sort().join(',');
  return {
    testAdmins: stats.testAdmins,
    neverChanged: stats.neverChangedPassword,
    total: stats.all,
    signature,
    needsLook: stats.testAdmins.length > 0,
  };
};

/* ----------------------------------------------------------------------------------------------
   Access (28 #1 grouped header · #4 draft + Review & apply · #9 phone accordion)
---------------------------------------------------------------------------------------------- */
/** Modules that are tabs (or a stage) inside a project page; everything else is an app page. */
export const PROJECT_MODULE_KEYS = ['stages', 'stage_lpo_loi', 'material', 'files', 'project_structure', 'execution', 'timeline', 'variations'];

/**
 * 28 #8 — the last sentence of the View · Edit note: which app pages also hide their edit buttons from a view-only
 * role (lib/permissions EDITABLE_PAGES), named, so the note stays true as more pages start using canEdit().
 * With today's EDITABLE_PAGES every page hides them (BUG-002 added Tasks and Shop Drawings):
 *   'Every page also hides its edit buttons from them.'
 * A page that still showed them would be named: 'Projects … also hide their edit buttons; Tasks still shows them for now.'
 */
export const editButtonsNote = (pages = EDITABLE_PAGES) => {
  const hiding = pages.filter((page) => page.hidesEditButtons).map((page) => page.label);
  const showing = pages.filter((page) => !page.hidesEditButtons).map((page) => page.label);
  if (!showing.length) return 'Every page also hides its edit buttons from them.';
  const still = `${joinWords(showing)} still ${showing.length === 1 ? 'shows' : 'show'} ${hiding.length ? 'them' : 'their edit buttons'} for now.`;
  if (!hiding.length) return still;
  return `${joinWords(hiding)} also ${hiding.length === 1 ? 'hides its' : 'hide their'} edit buttons; ${still}`;
};

export const accessModuleGroups = (modules = PERMISSION_MODULES) => {
  const list = modules.map((module) => (typeof module === 'string' ? { key: module, label: moduleLabel(module) } : module));
  const project = PROJECT_MODULE_KEYS.map((key) => list.find((module) => module.key === key)).filter(Boolean);
  const app = list.filter((module) => !PROJECT_MODULE_KEYS.includes(module.key));
  return [
    { key: 'app', label: 'App pages', modules: app },
    { key: 'project', label: 'Inside a project', modules: project },
  ];
};

const sortedKeys = (list) => [...new Set(list || [])].sort();
const sameKeys = (a, b) => {
  const left = sortedKeys(a);
  const right = sortedKeys(b);
  return left.length === right.length && left.every((key, index) => key === right[index]);
};

/** Toggle one module in a role's draft; the role leaves the draft when it matches the saved access. */
export const toggleAccessDraft = (draft = {}, saved = {}, role, moduleKey) => {
  const current = draft[role] || saved[role] || [];
  const next = current.includes(moduleKey) ? current.filter((key) => key !== moduleKey) : [...current, moduleKey];
  return setAccessDraft(draft, saved, role, next);
};

export const setAccessDraft = (draft = {}, saved = {}, role, permissions) => {
  const copy = { ...draft };
  if (sameKeys(permissions, saved[role] || [])) delete copy[role];
  else copy[role] = sortedKeys(permissions);
  return copy;
};

/** Per role: modules added / removed by the draft (roles in `order`). */
export const accessDraftChanges = (draft = {}, saved = {}, order = Object.keys(draft)) => order
  .filter((role) => draft[role])
  .map((role) => {
    const before = saved[role] || [];
    const after = draft[role];
    return {
      role,
      added: after.filter((key) => !before.includes(key)),
      removed: before.filter((key) => !after.includes(key)),
      permissions: after,
    };
  })
  .filter((change) => change.added.length || change.removed.length);

export const accessChangeCount = (changes = []) => changes.reduce(
  (sum, change) => sum + change.added.length + change.removed.length + (change.toView?.length || 0) + (change.toEdit?.length || 0), 0,
);

/** "Storekeeper + Execution · Site Engineer + Variations − Timeline" (+ "Procurement view only" with levels). */
export const accessChangeText = (change) => [
  change.role,
  ...change.added.map((key) => `+ ${moduleLabel(key)}${change.viewOnly?.includes(key) ? ' (view only)' : ''}`),
  ...change.removed.map((key) => `− ${moduleLabel(key)}`),
  ...(change.toView || []).map((key) => `${moduleLabel(key)} view only`),
  ...(change.toEdit || []).map((key) => `${moduleLabel(key)} can edit`),
].join(' ');

/* ----------------------------------------------------------------------------------------------
   Stage templates (29 #1 adopt · #3 pipeline overview · #4 Edit pipeline)
---------------------------------------------------------------------------------------------- */
export const REQUIRED_STAGE_NAMES = ['BOQ & Design', 'LPO/LOI', 'Handing over documents'];

const PHASE_OF_STAGE = {
  'received drawings': 'pre',
  'pre-qualification': 'pre',
  'material submittal': 'pre',
  'technical submittal': 'pre',
  'boq & design': 'award',
  'lpo/loi': 'award',
  'shop drawings': 'engineering',
  schematics: 'engineering',
  variations: 'close',
  'handing over documents': 'close',
};
export const PIPELINE_PHASES = [
  { key: 'pre', label: 'Pre-contract' },
  { key: 'award', label: 'Award' },
  { key: 'engineering', label: 'Engineering' },
  { key: 'close', label: 'Change & close-out' },
];

/**
 * The pipeline in phases: each known stage has its phase; a stage the company added joins the phase of
 * the stage before it. Consecutive stages of one phase form one bracket (`from`/`to` are 1-based).
 */
export const pipelinePhases = (stages = []) => {
  const brackets = [];
  let phase = 'pre';
  stages.forEach((stage, index) => {
    phase = PHASE_OF_STAGE[String(stage?.name || '').trim().toLowerCase()] || phase;
    const last = brackets[brackets.length - 1];
    if (last && last.key === phase) last.to = index + 1;
    else brackets.push({ key: phase, label: PIPELINE_PHASES.find((item) => item.key === phase).label, from: index + 1, to: index + 1 });
  });
  return brackets;
};

/** What the page shows as "the pipeline": the template when new projects use it, else the live one. */
export const effectivePipeline = (template) => {
  const sync = template?.sync;
  if (sync && sync.in_use === false && Array.isArray(sync.live_stages) && sync.live_stages.length) return sync.live_stages;
  return Array.isArray(template?.stages) ? template.stages : [];
};

let draftStageSeed = 0;
/** Draft rows carry a stable key (drag & drop, focus) and where they came from (for the change list). */
/*
  Stage SLA (04 #8, set here in Admin › Stage templates): whole days a stage may take before it is flagged on the
  project's Stages tab (backend/stage_sla.py). Empty = not set (an approval gate then uses the built-in 14 days,
  other stages have none); 0 = no SLA; at most 365.
*/
export const STAGE_SLA_MAX_DAYS = 365;
export const DEFAULT_APPROVAL_SLA_DAYS = 14;

const slaText = (value) => (value === null || value === undefined ? '' : String(value).trim());

/** The stored SLA of a template stage: a whole number 0…365, or null (not set). */
export const stageSlaDays = (stage) => {
  const text = slaText(stage?.sla_days);
  if (!text) return null;
  const number = Number(text);
  return Number.isInteger(number) && number >= 0 && number <= STAGE_SLA_MAX_DAYS ? number : null;
};

/** What the SLA column shows: '21 d', 'No SLA', 'Default · 14 d' (a gate without its own) or '—'. */
export const stageSlaLabel = (stage) => {
  const days = stageSlaDays(stage);
  if (days === 0) return 'No SLA';
  if (days !== null) return `${days} d`;
  return stage?.required_approval ? `Default · ${DEFAULT_APPROVAL_SLA_DAYS} d` : '—';
};

/** A problem with a typed SLA ('' when it is fine). */
export const stageSlaProblem = (value) => {
  const text = slaText(value);
  if (!text) return '';
  const number = Number(text);
  if (!Number.isFinite(number) || !Number.isInteger(number)) return 'SLA: whole days only';
  if (number < 0 || number > STAGE_SLA_MAX_DAYS) return `SLA: 0 to ${STAGE_SLA_MAX_DAYS} days`;
  return '';
};

export const pipelineDraft = (stages = []) => stages.map((stage, index) => {
  draftStageSeed += 1;
  return {
    key: `stage-${index}-${draftStageSeed}`,
    origin: String(stage.name || ''),
    name: String(stage.name || ''),
    description: String(stage.description || ''),
    required_approval: Boolean(stage.required_approval),
    approved_by: String(stage.approved_by || ''),
    sla_days: slaText(stage.sla_days),
  };
});

export const newPipelineStage = (name = '') => {
  draftStageSeed += 1;
  return { key: `new-${draftStageSeed}`, origin: '', name, description: '', required_approval: false, approved_by: '', sla_days: '', isNew: true };
};

/** The PUT body: names trimmed, order 1…N. Unknown keys of the saved stages are kept. */
export const pipelinePayload = (draft = [], saved = []) => draft.map((row, index) => {
  const base = saved.find((stage) => stage.name === row.origin) || {};
  const stage = {
    ...base,
    name: row.name.trim(),
    description: row.description.trim(),
    required_approval: Boolean(row.required_approval),
    order: index + 1,
  };
  // 29 #6: the draft's approver (only a gate keeps one)
  const approver = row.approved_by === undefined ? stage.approved_by : row.approved_by;
  if (stage.required_approval && approver) stage.approved_by = approver;
  else delete stage.approved_by;
  // 04 #8: the stage's SLA in days ('' = not set)
  if (row.sla_days !== undefined) {
    const text = slaText(row.sla_days);
    if (text) stage.sla_days = Number(text);
    else delete stage.sla_days;
  }
  return stage;
});

/** Readable problems that block Save: empty or duplicate names, a required stage missing. */
export const pipelineProblems = (draft = []) => {
  const problems = {};
  const seen = new Map();
  draft.forEach((row) => {
    const name = row.name.trim();
    if (!name) problems[row.key] = 'Give the stage a name';
    else if (name.length > 80) problems[row.key] = 'At most 80 characters';
    else if (seen.has(name.toLowerCase())) problems[row.key] = `“${name}” is used twice`;
    else if (stageSlaProblem(row.sla_days)) problems[row.key] = stageSlaProblem(row.sla_days);
    seen.set(name.toLowerCase(), row.key);
  });
  return problems;
};

/** One line per difference between the saved pipeline and the draft (for the unsaved bar). */
export const pipelineChanges = (saved = [], draft = []) => {
  const changes = [];
  const savedNames = saved.map((stage) => stage.name);
  const kept = draft.filter((row) => row.origin && savedNames.includes(row.origin));
  draft.forEach((row) => {
    const before = saved.find((stage) => stage.name === row.origin);
    const label = row.name.trim() || 'New stage';
    if (!before) {
      changes.push({ key: `add-${row.key}`, text: `${label}: added` });
      return;
    }
    if (row.name.trim() !== before.name) changes.push({ key: `name-${row.key}`, text: `${before.name} → ${label}` });
    if (row.description.trim() !== String(before.description || '').trim()) changes.push({ key: `desc-${row.key}`, text: `${label}: description edited` });
    if (Boolean(row.required_approval) !== Boolean(before.required_approval)) {
      changes.push({ key: `gate-${row.key}`, text: `${label}: ${row.required_approval ? 'approval gate' : 'no approval'}` });
    }
    if (row.required_approval && row.approved_by !== undefined && (row.approved_by || '') !== (before.approved_by || '')) {
      changes.push({ key: `approver-${row.key}`, text: `${label}: approved by ${approverLabel(null, row.approved_by) || 'nobody set'}` });
    }
    if (row.sla_days !== undefined && slaText(row.sla_days) !== slaText(before.sla_days)) {
      const next = slaText(row.sla_days);
      changes.push({ key: `sla-${row.key}`, text: `${label}: SLA ${next === '' ? 'not set' : next === '0' ? 'none' : `${next} d`}` });
    }
  });
  saved.forEach((stage) => {
    if (!draft.some((row) => row.origin === stage.name)) changes.push({ key: `del-${stage.name}`, text: `${stage.name}: removed` });
  });
  const keptOrder = kept.map((row) => row.origin);
  const savedOrder = savedNames.filter((name) => keptOrder.includes(name));
  if (keptOrder.some((name, index) => name !== savedOrder[index])) changes.push({ key: 'order', text: 'Order changed' });
  return changes;
};

export const moveItem = (list, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= list.length || to >= list.length) return list;
  const copy = [...list];
  const [item] = copy.splice(from, 1);
  copy.splice(to, 0, item);
  return copy;
};

/* ----------------------------------------------------------------------------------------------
   History (30 #1 sentences by day · #2 bursts · #5 summary strip · #6 grouped drawer)
---------------------------------------------------------------------------------------------- */
const COLLECTION_NOUNS = {
  boq_items: ['BOQ line', 'BOQ lines'],
  projects: ['project', 'projects'],
  shop_drawings: ['shop drawing', 'shop drawings'],
  tasks: ['task', 'tasks'],
  catalog: ['catalog item', 'catalog items'],
  stock: ['stock record', 'stock records'],
  stock_movements: ['stock movement', 'stock movements'],
  documents: ['document', 'documents'],
  site_requests: ['site request', 'site requests'],
  invoices: ['invoice', 'invoices'],
  variation_invoices: ['variation invoice', 'variation invoices'],
  variations: ['variation', 'variations'],
  users: ['person', 'people'],
  role_permissions: ['role access', 'role access'],
  stage_templates: ['stage template', 'stage templates'],
  app_settings: ['app setting', 'app settings'],
  lpos: ['LPO', 'LPOs'],
  delivery_notes: ['delivery note', 'delivery notes'],
  delivery_plans: ['delivery plan', 'delivery plans'],
  reservations: ['reservation', 'reservations'],
  suppliers: ['supplier', 'suppliers'],
  ai_chats: ['AI chat', 'AI chats'],
};

export const collectionNoun = (collection, count = 2) => {
  const nouns = COLLECTION_NOUNS[collection] || [String(collection || 'record').replace(/_/g, ' '), String(collection || 'records').replace(/_/g, ' ')];
  return Number(count) === 1 ? nouns[0] : nouns[1];
};

const COUNT_VERBS = { created: 'added', updated: 'updated', deleted: 'deleted' };

/**
 * "boq_items: 4 created; stock: 1 created, 2 updated" → [{ collection, count, verb }]
 * (verbs: added / updated / deleted). Text that is not in this shape gives [].
 */
export const parseChangeCounts = (summary) => {
  const parts = [];
  String(summary || '').split(';').forEach((chunk) => {
    const match = chunk.trim().match(/^([a-z_]+):\s*(.+)$/);
    if (!match) return;
    match[2].split(',').forEach((piece) => {
      const counted = piece.trim().match(/^(\d+)\s+(created|updated|deleted)$/);
      if (counted) parts.push({ collection: match[1], count: Number(counted[1]), verb: COUNT_VERBS[counted[2]] });
    });
  });
  return parts;
};

// Execution-structure steps: "Added device: Luxor Tower > Floor - 3RD PODIUM > …" (not field updates such as "Updated global: updated_at").
const STRUCTURE_PART = /^(Added|Removed|Updated|Deleted|Moved|Renamed) (device|field device|network panel|panel|floor|building|outlet|controller|point|zone|room|system|cabinet): (.+)$/;

/** "Added device: Luxor Tower > Floor - 3RD PODIUM > DDC - … ; … ; Added device (+32 more)" */
export const parseStructureSummary = (summary) => {
  const chunks = String(summary || '').split(';').map((chunk) => chunk.trim()).filter(Boolean);
  if (!chunks.length || !STRUCTURE_PART.test(chunks[0])) return null;
  let total = 0;
  const floors = [];
  chunks.forEach((chunk) => {
    const more = chunk.match(/\(\+(\d+) more\)$/);
    if (more) {
      total += Number(more[1]);
      return;
    }
    const match = chunk.match(STRUCTURE_PART);
    if (!match) return;
    total += 1;
    const floor = match[3].split('>').map((part) => part.trim()).find((part) => /^floor\b/i.test(part));
    const name = floor ? floor.replace(/^floor\s*-\s*/i, '') : '';
    if (name && !floors.includes(name)) floors.push(name);
  });
  return { total, floors, verb: chunks[0].match(STRUCTURE_PART)[1].toLowerCase() };
};

/** Change counts as words: "4 BOQ lines added · 1 stock record updated". */
export const humanizeChangeSummary = (summary) => {
  const counts = parseChangeCounts(summary);
  if (counts.length) return counts.map((part) => `${part.count} ${collectionNoun(part.collection, part.count)} ${part.verb}`).join(' · ');
  const structure = parseStructureSummary(summary);
  if (structure) {
    const where = structure.floors.length ? ` on ${shortList(structure.floors, 3).join(', ')}` : '';
    return `${structure.total} structure ${structure.total === 1 ? 'change' : 'changes'}${where}`;
  }
  return String(summary || '').trim();
};

const VERB_PAST = {
  create: 'Created', update: 'Updated', delete: 'Deleted', import: 'Imported', duplicate: 'Duplicated',
  attach: 'Attached', remove: 'Removed', mark: 'Marked', move: 'Moved', log: 'Logged', record: 'Recorded',
  comment: 'Commented', reset: 'Reset', correct: 'Corrected', add: 'Added', merge: 'Merged', set: 'Set',
  change: 'Changed', adopt: 'Adopted', link: 'Linked', unlink: 'Unlinked', assign: 'Assigned', upload: 'Uploaded',
  approve: 'Approved', reject: 'Rejected', submit: 'Submitted', cancel: 'Cancelled', reorder: 'Reordered',
  rename: 'Renamed', restore: 'Restored', complete: 'Completed', reopen: 'Reopened', sync: 'Synced',
  turn: 'Turned', switch: 'Switched', send: 'Sent', chase: 'Chased', pin: 'Pinned', unpin: 'Unpinned',
  raise: 'Raised', receive: 'Received', scan: 'Scanned', lock: 'Locked', apply: 'Applied', copy: 'Copied',
  edit: 'Edited', save: 'Saved', confirm: 'Confirmed', dismiss: 'Dismissed', convert: 'Converted', close: 'Closed',
  issue: 'Issued', return: 'Returned', release: 'Released', reserve: 'Reserved', enable: 'Enabled', disable: 'Disabled',
  book: 'Booked', give: 'Gave',
};

const pastTense = (label) => {
  const text = String(label || '').trim();
  const [first, ...rest] = text.split(/\s+/);
  if (first?.toLowerCase() === 'bulk' && rest.length) return `Bulk-${pastTense(rest.join(' ')).replace(/^\w/, (c) => c.toLowerCase())}`;
  if (first?.toLowerCase() === 'inline' && rest[0]?.toLowerCase() === 'edit') return `Edited ${rest.slice(1).join(' ')}`.trim();
  const past = VERB_PAST[first?.toLowerCase()];
  return past ? [past, ...rest].join(' ') : text;
};

/**
 * One readable sentence for a history entry: { lead, target } → "Updated execution structure — **Luxor Tower**".
 * "Restore X" (an Undo) reads "Undid “X”". The target is left out when the label already names it.
 */
const UUID_IN_TEXT = /\s*\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/gi;
/** A value that is only a record id (UUID, Mongo ObjectId, long hex) — never shown to people. */
export const isRecordId = (value) => /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24}|[0-9a-f]{32})$/i
  .test(String(value || '').trim());

export const historySentence = (item = {}) => {
  const target = historyTarget(item);
  // older entries carry an id in the label ("Delete user 71d9d6ea-…"): the item's name stands in, else nothing
  let label = String(item.label || 'Change').trim();
  if (UUID_IN_TEXT.test(label)) {
    const name = String(item.entity_label || '').trim();
    const usable = name && !GENERIC_HISTORY_TARGETS.has(name.toLowerCase()) && !isRecordId(name);
    label = label.replace(UUID_IN_TEXT, usable ? ` ${name}` : '').trim() || 'Change';
  }
  UUID_IN_TEXT.lastIndex = 0;
  const restore = label.match(/^Restore (.+)$/);
  let lead;
  if (restore) lead = `Undid “${pastTense(restore[1])}”`;
  else lead = pastTense(label);
  const mentions = target && lead.toLowerCase().includes(target.toLowerCase());
  return { lead, target: mentions ? '' : target };
};

/** The Size column: "+1 project", "−3 BOQ lines", "4 shop drawings", "52 items". */
export const historySize = (item = {}) => {
  const counts = parseChangeCounts(item.change_summary);
  if (counts.length === 1) {
    const [part] = counts;
    const sign = part.verb === 'added' ? '+' : part.verb === 'deleted' ? '−' : '';
    return `${sign}${part.count} ${collectionNoun(part.collection, part.count)}`;
  }
  if (counts.length > 1) {
    const total = counts.reduce((sum, part) => sum + part.count, 0);
    return `${total} records`;
  }
  const structure = parseStructureSummary(item.change_summary);
  if (structure) return `${structure.total} ${structure.total === 1 ? 'item' : 'items'}`;
  return '1 change';
};

export const BURST_GAP_MS = 15 * 60 * 1000;
const instant = (value) => {
  const ms = Date.parse(value || '');
  return Number.isFinite(ms) ? ms : NaN;
};

/**
 * Feed rows: runs of changes by the same person in the same area, each within 15 minutes of the next,
 * become one burst row (3 or more changes, or 2 with the same label). Items are newest first.
 */
export const burstHistory = (items = [], { gapMs = BURST_GAP_MS } = {}) => {
  const rows = [];
  let run = [];
  const flush = () => {
    if (!run.length) return;
    const sameLabel = run.every((item) => item.label === run[0].label);
    if (run.length >= 3 || (run.length === 2 && sameLabel)) rows.push({ type: 'burst', key: `burst-${run[0].id}`, items: run });
    else run.forEach((item) => rows.push({ type: 'item', key: item.id, item }));
    run = [];
  };
  items.forEach((item) => {
    const previous = run[run.length - 1];
    const joins = previous
      && (previous.user_id || previous.user_name) === (item.user_id || item.user_name)
      && (previous.page_type || '') === (item.page_type || '')
      && Math.abs(instant(previous.created_at) - instant(item.created_at)) <= gapMs;
    if (!joins) flush();
    run.push(item);
  });
  flush();
  return rows;
};

const ACTION_VERB = { import: 'Imported', create: 'Added', delete: 'Deleted', update: 'Updated', restore: 'Undid' };

/**
 * A burst in one sentence: "Imported 129 BOQ lines and 20 catalog items · Deleted 42 BOQ lines",
 * or "Updated 38 projects" when every change is the same edit on a different target.
 */
export const burstSummary = (items = []) => {
  const targets = [];
  items.forEach((item) => {
    const target = historyTarget(item);
    if (target && !targets.includes(target)) targets.push(target);
  });
  const groups = new Map();
  const uncounted = [];
  items.forEach((item) => {
    const action = item.action_type || 'update';
    const verb = ACTION_VERB[action] || 'Changed';
    const counts = parseChangeCounts(item.change_summary);
    if (!counts.length) {
      uncounted.push(item);
      return;
    }
    counts.forEach((part) => {
      // an import's "created" rows are what was imported; its "updated" rows (stock) are not the point
      if (action === 'import' && part.verb !== 'added') return;
      const partVerb = action === 'import' || action === 'restore' ? verb : ACTION_VERB[{ added: 'create', deleted: 'delete', updated: 'update' }[part.verb]];
      const key = `${partVerb}|${part.collection}`;
      groups.set(key, { verb: partVerb, collection: part.collection, count: (groups.get(key)?.count || 0) + part.count });
    });
  });
  const byVerb = new Map();
  groups.forEach((group) => {
    const noun = group.collection ? collectionNoun(group.collection, group.count) : `${group.count === 1 ? 'change' : 'changes'}`;
    const words = `${group.count} ${noun}`;
    byVerb.set(group.verb, [...(byVerb.get(group.verb) || []), words]);
  });
  const sameLabel = items.every((item) => item.label === items[0].label);
  const onlyProjects = [...groups.values()].every((group) => group.collection === 'projects' || !group.collection);
  if (!groups.size) {
    // nothing counted (sign-in switches, settings …): one label repeated → "Turned off sign-in for X · 3 times"
    if (sameLabel) return { text: `${historySentence(items[0]).lead} · ${items.length} times`, targets, joiner: '' };
    return { text: `${items.length} changes`, targets, joiner: 'on' };
  }
  if (sameLabel && targets.length > 1 && onlyProjects) {
    const verb = pastTense(items[0].label).split(' ')[0];
    return { text: `${verb} ${targets.length} projects`, targets, joiner: '' };
  }
  // the same project edited again and again: "13 edits to Luxor Tower"
  if (targets.length === 1 && onlyProjects && items.every((item) => (item.action_type || 'update') === 'update')) {
    return { text: `${items.length} edits`, targets, joiner: 'to' };
  }
  const text = [
    ...[...byVerb.entries()].map(([verb, words], index) => `${index ? verb.toLowerCase() : verb} ${joinWords(words)}`),
    uncounted.length ? `${uncounted.length} other ${uncounted.length === 1 ? 'change' : 'changes'}` : '',
  ].filter(Boolean).join(' · ');
  return { text: text || `${items.length} changes`, targets, joiner: 'on' };
};

export const durationWords = (ms) => {
  const minutes = Math.max(1, Math.round(ms / 60000));
  if (minutes < 60) return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  if (minutes % 60 === 0) return `${minutes / 60} ${minutes === 60 ? 'hour' : 'hours'}`;
  if (minutes < 120) return `${minutes} min`; // 89 min, not "1.5 hours"
  return `about ${Math.round(minutes / 60)} hours`; // never "20.6 hours"
};

export const HISTORY_AREA_TONES = { procurement: 'navy', project: 'blue', inventory: 'teal', admin: 'violet', shop_drawings: 'amber' };

/** Summary strip: changes per area (largest first) and the most active people. */
export const historyBreakdown = (items = []) => {
  const areas = new Map();
  const people = new Map();
  items.forEach((item) => {
    const area = item.page_type || '';
    areas.set(area, (areas.get(area) || 0) + 1);
    const key = item.user_id || item.user_name || 'unknown';
    const person = people.get(key) || { id: item.user_id || '', name: item.user_name || 'Unknown person', count: 0 };
    person.count += 1;
    people.set(key, person);
  });
  const total = items.length;
  return {
    total,
    areas: [...areas.entries()]
      .map(([key, count]) => ({ key, label: historyAreaLabel(key), count, share: total ? count / total : 0 }))
      .sort((a, b) => b.count - a.count),
    people: [...people.values()].sort((a, b) => b.count - a.count),
  };
};

/**
 * The strip for a whole period from GET /admin/history/insights (counted on the server, not just the loaded page):
 * same shape as historyBreakdown. null when the insights do not carry totals (an older server).
 */
export const insightsBreakdown = (insights) => {
  if (!insights || !Number.isFinite(Number(insights.total)) || !Array.isArray(insights.areas)) return null;
  const total = Number(insights.total);
  return {
    total,
    areas: insights.areas
      .map((area) => ({ key: area.key || '', label: historyAreaLabel(area.key || ''), count: Number(area.count) || 0 }))
      .map((area) => ({ ...area, share: total ? area.count / total : 0 }))
      .sort((a, b) => b.count - a.count),
    people: (insights.people || [])
      .map((person) => ({ id: person.user_id || '', name: person.name || 'Unknown person', count: Number(person.count) || 0 }))
      .sort((a, b) => b.count - a.count),
  };
};

// Same rule as the server (routes/admin.py TECHNICAL_FIELD): ids, tokens and the *_updated_at / *_updated_by … of any part.
const TECHNICAL_FIELD = /(^|\.)(updated_at|created_at|updated_by|created_by|_id|id|save_token|execution_structure_save_token|[a-z0-9_]*_(?:updated|created)_(?:at|by)|buildings)$/i;

const STEP_KIND_NOUNS = {
  device: ['device', 'devices'],
  'field device': ['field device', 'field devices'],
  'network panel': ['DDC panel', 'DDC panels'],
  panel: ['panel', 'panels'],
  floor: ['floor', 'floors'],
  building: ['building', 'buildings'],
  outlet: ['outlet', 'outlets'],
};

/**
 * Structure steps ("Added device: Luxor Tower > Floor - 3RD PODIUM > DDC - 3RD PODIUM > Pump Set - None - IRRIGATION PUMP SET")
 * grouped by floor › panel, with badge counts ("+35 devices", "+4 DDC panels", "4 floors").
 * Other steps stay a flat list; technical fields (updated_at…) are counted, not shown.
 */
export const groupHistorySteps = (steps = []) => {
  const groups = new Map();
  const others = [];
  const kinds = new Map();
  let hidden = 0;
  const hiddenNames = [];
  steps.forEach((step) => {
    const match = String(step.label || '').match(STRUCTURE_PART);
    if (!match) {
      const field = String(step.label || '').match(/^Updated [^:]+: (.+)$/)?.[1] || step.field_path || '';
      if (field && TECHNICAL_FIELD.test(field.trim())) {
        hidden += 1;
        hiddenNames.push(field.trim());
        return;
      }
      others.push(step);
      return;
    }
    const [, verbWord, kind, path] = match;
    const verb = verbWord.toLowerCase();
    const parts = path.split('>').map((part) => part.trim());
    const floor = parts.find((part) => /^floor\b/i.test(part)) || '';
    const panel = parts.find((part, index) => index > parts.indexOf(floor) && /^(ddc|panel|lcp|mcc)\b/i.test(part)) || '';
    const leaf = parts[parts.length - 1];
    // "Pump Set - None - IRRIGATION PUMP SET" → name "IRRIGATION PUMP SET", type "Pump Set"
    const leafParts = leaf.split(' - ').map((piece) => piece.trim()).filter((piece) => piece && piece !== 'None');
    const name = leafParts.length > 1 ? leafParts[leafParts.length - 1] : leaf;
    const type = leafParts.length > 1 ? leafParts[0] : '';
    const key = `${floor}|${panel}`;
    if (!groups.has(key)) groups.set(key, { key, floor: floor.replace(/^floor\s*-\s*/i, ''), panel, newPanel: false, lines: [] });
    const group = groups.get(key);
    const kindKey = `${verb}|${kind}`;
    kinds.set(kindKey, { verb, kind, count: (kinds.get(kindKey)?.count || 0) + 1 });
    if (kind === 'network panel' && verb === 'added' && leaf === panel) {
      group.newPanel = true;
      group.panelStep = step;
      return;
    }
    group.lines.push({ step, verb, kind, name, type });
  });
  const floors = new Set([...groups.values()].map((group) => group.floor).filter(Boolean));
  const badges = [...kinds.values()].map(({ verb, kind, count }) => {
    const nouns = STEP_KIND_NOUNS[kind] || [kind, `${kind}s`];
    const sign = verb === 'added' ? '+' : verb === 'removed' || verb === 'deleted' ? '−' : '';
    return { key: `${verb}-${kind}`, tone: sign === '+' ? 'green' : sign ? 'red' : 'gray', text: `${sign}${count} ${count === 1 ? nouns[0] : nouns[1]}${sign ? '' : ` ${verb}`}` };
  });
  if (floors.size) badges.push({ key: 'floors', tone: 'gray', text: `${floors.size} ${floors.size === 1 ? 'floor' : 'floors'}` });
  return { groups: [...groups.values()], others, badges, hidden, hiddenNames };
};

/**
 * Numbered series read as a range: ['CO SENSOR #1', …, 'CO SENSOR #8', 'SPF-01A'] → ['CO SENSOR #1 – #8', 'SPF-01A']
 * (three or more consecutive numbers with the same prefix; anything else is listed as it is, in order).
 */
export const compressNames = (names = []) => {
  const out = [];
  let run = null;
  const flush = () => {
    if (!run) return;
    if (run.items.length >= 3) out.push(`${run.prefix}${run.first} – ${/#\s*$/.test(run.prefix) ? '#' : ''}${run.last}`);
    else out.push(...run.items);
    run = null;
  };
  names.forEach((name) => {
    const match = String(name || '').match(/^(.*?)(\d+)$/);
    const number = match ? Number(match[2]) : NaN;
    if (run && match && match[1] === run.prefix && number === run.last + 1) {
      run.items.push(name);
      run.last = number;
      return;
    }
    flush();
    if (match) run = { prefix: match[1], first: number, last: number, items: [name] };
    else out.push(name);
  });
  flush();
  return out;
};

/**
 * 30 #6 — one floor box's lines merged by what happened and to what: "+ Fan · SPF-01A, SPF-01B, … · 4 devices",
 * "+ Co Sensor · CO SENSOR #1 – #8 · 8 field devices". Each group keeps its lines (for "Undo step").
 */
export const groupFloorLines = (lines = []) => {
  const groups = new Map();
  lines.forEach((line) => {
    const key = `${line.verb}|${line.type || ''}|${line.kind}`;
    if (!groups.has(key)) groups.set(key, { key, verb: line.verb, type: line.type || '', kind: line.kind, lines: [] });
    groups.get(key).lines.push(line);
  });
  return [...groups.values()].map((group) => {
    const nouns = STEP_KIND_NOUNS[group.kind] || [group.kind, `${group.kind}s`];
    const sorted = [...group.lines].sort((a, b) => String(a.name).localeCompare(String(b.name), 'en', { numeric: true }));
    return {
      ...group,
      names: compressNames(sorted.map((line) => line.name)),
      countText: `${group.lines.length} ${group.lines.length === 1 ? nouns[0] : nouns[1]}`,
    };
  });
};

/* ----------------------------------------------------------------------------------------------
   History › Export CSV
---------------------------------------------------------------------------------------------- */
// Same rule as the other CSV exports (documents-ai/csv.js, inventory, supply-finance): a cell that starts
// with = + - @ (or a tab / carriage return) gets a leading apostrophe, so a project or person called
// "=HYPERLINK(…)" opens in Excel as text instead of running. Plain numbers ("-3", "+1,250.5") stay numbers.
const CSV_FORMULA_START = /^[=+\-@\t\r]/;
const CSV_PLAIN_NUMBER = /^[+-]?(?:\d{1,3}(?:,\d{3})+|\d+)(?:\.\d+)?%?$/;

/** One CSV cell: formulas neutralised; quoted when it holds a comma, quote or line break (\n or \r). */
export const csvCell = (value) => {
  let text = String(value ?? '');
  if (CSV_FORMULA_START.test(text) && !CSV_PLAIN_NUMBER.test(text.trim())) text = `'${text}`;
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const historyCsvHeader = (zoneShort = 'GST') => [
  `Date (${zoneShort || 'GST'})`, `Time (${zoneShort || 'GST'})`, 'Person', 'What changed', 'Project / item', 'Area', 'Action', 'Status', 'Details',
];
export const HISTORY_CSV_HEADER = historyCsvHeader('GST');

/** The loaded History rows as CSV (header first, CRLF-free, one row per change); zoneShort labels the time zone. */
export const historyCsv = (items = [], { zoneShort = 'GST' } = {}) => {
  const lines = items.map((item) => {
    const { lead, target } = historySentence(item);
    return [
      toDateInputValue(item.created_at), formatTime(item.created_at), item.user_name, lead, target,
      historyAreaLabel(item.page_type), optionLabel(HISTORY_ACTIONS, item.action_type),
      optionLabel(HISTORY_STATUSES, item.status || 'available'), humanizeChangeSummary(item.change_summary),
    ].map(csvCell).join(',');
  });
  return [historyCsvHeader(zoneShort).map(csvCell).join(','), ...lines].join('\n');
};

/* Users › Export CSV (27) — the people shown, with what the table says about them. */
export const USERS_CSV_HEADER = ['Name', 'Email / login', 'Role', 'Role family', 'Password changed', 'Created', 'Last sign-in', 'Changes · 14 days', 'Sign-in'];

export const usersCsv = (users = [], { activity = null, trackingSince = '', now = new Date() } = {}) => {
  const familyLabel = (role) => ROLE_FAMILIES.find((family) => family.key === roleFamily(role))?.label || '';
  const lines = (users || []).map((user) => [
    user.name, userLoginLabel(user), user.role, familyLabel(user.role),
    user.password_changed_at ? formatDate(user.password_changed_at) : 'Never',
    formatDate(user.created_at, { fallback: '' }),
    user.last_login_at ? formatDate(user.last_login_at) : lastSignInText(user, { tracking: Boolean(trackingSince), trackingSince, now }),
    activity ? changesFor(activity, user) : '',
    isSignInOff(user) ? 'Off' : 'On',
  ].map(csvCell).join(','));
  return [USERS_CSV_HEADER.map(csvCell).join(','), ...lines].join('\n');
};

/* ----------------------------------------------------------------------------------------------
   Integrations (31 #1 two cards · #3 QuickBooks setup checklist)
---------------------------------------------------------------------------------------------- */
/** The four QuickBooks setup steps with their state: 'done' · 'todo' · 'blocked' · 'locked' · 'waiting'. */
export const quickBooksSteps = (status, sync) => {
  const configured = Boolean(status?.configured);
  const connected = Boolean(status?.connected);
  // the default service item is step 3's, not one of step 1's keys (the callout still counts every key for IT)
  const missing = (status?.missing || []).filter((key) => key !== 'QUICKBOOKS_DEFAULT_ITEM_ID');
  const keyMissing = configured ? [] : (missing.length ? missing : QUICKBOOKS_KEYS.filter((key) => key !== 'QUICKBOOKS_DEFAULT_ITEM_ID'));
  const synced = Number(sync?.synced || 0);
  const steps = [
    { key: 'keys', title: 'Server keys', state: configured ? 'done' : 'blocked', missing: keyMissing },
    { key: 'connect', title: 'Connect your QuickBooks company', state: connected ? 'done' : configured ? 'todo' : 'locked' },
    { key: 'item', title: 'Default service item', state: status?.has_default_item ? 'done' : 'waiting' },
    { key: 'sync', title: 'First invoice sync', state: synced > 0 ? 'done' : connected ? 'todo' : 'waiting', synced },
  ];
  return { steps, done: steps.filter((step) => step.state === 'done').length };
};

/* ----------------------------------------------------------------------------------------------
   Users — wave 3 (27 #8 changes per person · #9 sign-in off / on, last sign-in)
---------------------------------------------------------------------------------------------- */
export const isSignInOff = (user) => Boolean(user?.sign_in_disabled);

/** Sign-ins are recorded from the first sign-in after the feature shipped: until then nobody has one. */
export const signInTrackingStarted = (users = []) => (users || []).some((user) => Boolean(user?.last_login_at));

/**
 * The line under "Created" and its tone:
 *   'Sign-in off since 28 Sep' (off) · 'Signed in today' / 'Signed in 28 Sep' (muted) ·
 *   'Never signed in' (red) — only for an account created after sign-ins started being recorded (trackingSince),
 *   'No sign-in since 2 Oct' (muted) — an older account that has not signed in since recording started; it may
 *   well have signed in before, so it is not alarming · '' when nothing is known.
 * Without trackingSince (an older server) 'tracking' keeps the old rule.
 */
export const lastSignIn = (user = {}, { tracking = false, trackingSince = '', now = new Date() } = {}) => {
  if (isSignInOff(user)) {
    return {
      tone: 'off',
      text: user.sign_in_disabled_at ? `Sign-in off since ${formatDate(user.sign_in_disabled_at, { year: 'auto', now })}` : 'Sign-in off',
    };
  }
  if (user.last_login_at) {
    return {
      tone: 'muted',
      text: toDateInputValue(user.last_login_at) === toDateInputValue(now)
        ? 'Signed in today'
        : `Signed in ${formatDate(user.last_login_at, { year: 'auto', now })}`,
    };
  }
  if (trackingSince) {
    const created = Date.parse(user.created_at || '');
    const since = Date.parse(trackingSince);
    if (Number.isFinite(created) && Number.isFinite(since) && created >= since) return { tone: 'red', text: 'Never signed in' };
    return { tone: 'muted', text: `No sign-in since ${formatDate(trackingSince, { year: 'auto', now })}` };
  }
  return tracking ? { tone: 'red', text: 'Never signed in' } : { tone: 'muted', text: '' };
};

export const lastSignInText = (user = {}, options = {}) => lastSignIn(user, options).text;

/** "Changes · 14 days" cell value: a number, 0 for nobody. */
export const changesFor = (counts, user) => Number((counts || {})[user?.id] || 0);

/* ----------------------------------------------------------------------------------------------
   Access — wave 3 (28 #6 Preview as role · #8 View / Edit levels)
---------------------------------------------------------------------------------------------- */
// Every module with changes to make (permissions.LEVEL_MODULES) — what a read-only role is View in, when the server
// does not send level_modules + level_modules_soon. Where View can be SET comes only from the server (level_modules).
export const LEVEL_MODULE_KEYS = [
  'projects', 'tasks', 'shop_drawings', 'files', 'inventory', 'procurement', 'forecast', 'invoicing',
  'stages', 'material', 'project_structure', 'execution', 'timeline', 'variations',
];
export const READ_ONLY_ROLES = ['Viewer'];

/** 'edit' | 'view' | null for one cell. */
export const accessLevel = (permissions = [], viewOnly = [], moduleKey) => {
  if (!(permissions || []).includes(moduleKey)) return null;
  return (viewOnly || []).includes(moduleKey) ? 'view' : 'edit';
};

/**
 * Next level when a cell is clicked in View · Edit mode: Edit → View → none → Edit. One click turns full
 * access into "view only" (the usual change); a tick from none gives Edit, as in On / Off mode.
 */
export const nextAccessLevel = (level) => (level === 'edit' ? 'view' : level === 'view' ? null : 'edit');

/** A role's view-only list in the level draft; the role leaves the draft when it matches the saved list. */
export const setLevelDraft = (levelDraft = {}, savedView = {}, role, viewOnly) => {
  const copy = { ...levelDraft };
  if (sameKeys(viewOnly, savedView[role] || [])) delete copy[role];
  else copy[role] = sortedKeys(viewOnly);
  return copy;
};

/** The view-only modules a role ends up with: the draft's (or the saved) list, limited to what it opens. */
export const effectiveViewOnly = (role, permissions = [], levelDraft = {}, savedView = {}) => (
  (levelDraft[role] || savedView[role] || []).filter((key) => permissions.includes(key))
);

/**
 * Every role the drafts change, with what changes — on / off (added, removed) and levels (toView, toEdit;
 * a module added as view-only is also in `viewOnly`). Same shape as accessDraftChanges plus `view_only`
 * (the role's full view-only list, sent to the server).
 */
export const accessAndLevelChanges = ({ draft = {}, levelDraft = {}, saved = {}, savedView = {}, roles = [] } = {}) => roles
  .filter((role) => draft[role] || levelDraft[role])
  .map((role) => {
    const before = saved[role] || [];
    const after = draft[role] || before;
    const viewBefore = (savedView[role] || []).filter((key) => before.includes(key));
    const viewAfter = effectiveViewOnly(role, after, levelDraft, savedView);
    const kept = after.filter((key) => before.includes(key));
    return {
      role,
      added: after.filter((key) => !before.includes(key)),
      removed: before.filter((key) => !after.includes(key)),
      toView: kept.filter((key) => viewAfter.includes(key) && !viewBefore.includes(key)),
      toEdit: kept.filter((key) => viewBefore.includes(key) && !viewAfter.includes(key)),
      viewOnly: viewAfter,
      permissions: after,
      view_only: viewAfter,
    };
  })
  .filter((change) => change.added.length || change.removed.length || change.toView.length || change.toEdit.length);

// What a role sees — the sidebar (Layout NAV_ITEMS order) and the project page tabs.
export const PREVIEW_PAGES = [
  { key: 'dashboard', label: 'Dashboard', permission: 'dashboard' },
  { key: 'tasks', label: 'Tasks', permission: 'tasks' },
  { key: 'projects', label: 'Projects', permission: 'projects' },
  { key: 'shop_drawings', label: 'Shop Drawings', permission: 'shop_drawings' },
  { key: 'documents', label: 'Documents', permission: 'files' },
  { key: 'inventory', label: 'Inventory', permission: 'inventory' },
  { key: 'procurement', label: 'Procurement', permission: 'procurement' },
  { key: 'forecast', label: 'Forecast', permission: 'forecast' },
  { key: 'invoicing', label: 'Invoicing', permission: 'invoicing' },
  { key: 'ai', label: 'AI', permission: 'ai' },
  { key: 'admin', label: 'Admin', permission: 'admin' },
];
export const PREVIEW_PROJECT_TABS = [
  { key: 'stages', label: 'Stages', permission: 'stages' },
  { key: 'material', label: 'Material', permission: 'material' },
  { key: 'files', label: 'Files', permission: 'files' },
  { key: 'invoicing', label: 'Invoicing', permission: 'invoicing' },
  { key: 'structure', label: 'Structure', permission: 'project_structure' },
  { key: 'execution', label: 'Execution', permission: 'execution' },
  { key: 'timeline', label: 'Timeline', permission: 'timeline' },
  { key: 'variations', label: 'Variations', permission: 'variations' },
];

const opensModule = (permissions, key) => permissions.includes(key) || (key === 'shop_drawings' && permissions.includes('tasks'));

/**
 * 28 #6 — what one role sees with the draft applied: sidebar pages and project tabs, each { visible,
 * level ('edit' | 'view'), change ('new' | 'removed' | 'to-view' | 'to-edit' | '') }, plus a one-line summary.
 */
export const rolePreview = ({ role = '', savedPermissions = [], permissions = [], savedView = [], viewOnly = [], superAdmin = false } = {}) => {
  const allKeys = PERMISSION_MODULES.map((module) => module.key);
  const now = superAdmin ? allKeys : permissions;
  const before = superAdmin ? allKeys : savedPermissions;
  const levelOf = (list, views, key) => {
    const target = key === 'shop_drawings' && !list.includes('shop_drawings') ? 'tasks' : key;
    return views.includes(target) ? 'view' : 'edit';
  };
  const describe = (item) => {
    const visible = opensModule(now, item.permission);
    const was = opensModule(before, item.permission);
    const level = visible ? levelOf(now, superAdmin ? [] : viewOnly, item.permission) : null;
    const wasLevel = was ? levelOf(before, superAdmin ? [] : savedView, item.permission) : null;
    let change = '';
    if (visible && !was) change = 'new';
    else if (!visible && was) change = 'removed';
    else if (visible && level !== wasLevel) change = level === 'view' ? 'to-view' : 'to-edit';
    return { ...item, visible, level, change };
  };
  const pages = PREVIEW_PAGES.map(describe);
  const tabs = PREVIEW_PROJECT_TABS.map(describe);
  const unique = (list) => [...new Set(list)];
  const gained = unique([...pages, ...tabs].filter((item) => item.change === 'new').map((item) => item.label));
  const lost = unique([...pages, ...tabs].filter((item) => item.change === 'removed').map((item) => item.label));
  const toView = unique([...pages, ...tabs].filter((item) => item.change === 'to-view').map((item) => item.label));
  const stillHidden = unique([...pages, ...tabs].filter((item) => !item.visible && !item.change).map((item) => item.label));
  const people = /s$/.test(role) ? role : `${role}s`;
  let summary;
  if (!gained.length && !lost.length && !toView.length && !pages.concat(tabs).some((item) => item.change === 'to-edit')) {
    summary = `No changes for ${people} in the draft — this is what they see today.`;
  } else {
    const parts = [];
    if (gained.length) parts.push(`will be able to open ${joinWords(gained)}`);
    if (lost.length) parts.push(`will no longer see ${joinWords(lost)}`);
    if (toView.length) parts.push(`will only view ${joinWords(toView)}`);
    if (!parts.length) parts.push('can edit again what they could only view');
    const notYet = gained.length && stillHidden.length ? `, but still not ${joinWords(stillHidden.slice(0, 2))}` : '';
    summary = `${people} ${parts.join(' and ')}${notYet}.`;
  }
  return { pages, tabs, gained, lost, summary };
};

/* ----------------------------------------------------------------------------------------------
   Stage templates — wave 3 (29 #6 Approved by · #7 active projects · #8 a template per system type)
---------------------------------------------------------------------------------------------- */
export const FALLBACK_APPROVERS = [
  { value: 'client_consultant', label: 'Client / Consultant' },
  { value: 'consultant', label: 'Consultant' },
  { value: 'client', label: 'Client' },
  { value: 'pm', label: 'PM' },
  { value: 'internal_senior_site_engineer', label: 'Internal · Senior Site Engineer' },
  { value: 'internal_pm', label: 'Internal · PM' },
  { value: 'internal_admin', label: 'Internal · Admin' },
];

export const approverLabel = (approvers, value) => (
  (approvers && approvers.length ? approvers : FALLBACK_APPROVERS).find((option) => option.value === value)?.label || ''
);

/** The stage list with one stage's fields changed (the PUT body of one switch / approver change). */
export const withStagePatch = (stages = [], index, patch) => stages.map((stage, itemIndex) => {
  if (itemIndex !== index) return stage;
  const next = { ...stage, ...patch };
  if (!next.required_approval || !next.approved_by) delete next.approved_by; // only a gate has an approver
  return next;
});

/**
 * Template chips (29 #8): one chip per system type that has projects — `template` when it has its own,
 * else an "add" chip. Projects without a system type are counted in `unassigned` (no template for them).
 */
export const systemTemplateChips = (systemTypeCounts = [], systemTemplates = []) => {
  const templates = new Map((systemTemplates || []).map((item) => [item.system_key, item]));
  const total = (systemTypeCounts || []).reduce((sum, item) => sum + (Number(item.projects) || 0), 0);
  const chips = (systemTypeCounts || [])
    .filter((item) => item.system_key)
    .map((item) => ({
      key: item.system_key,
      systemType: item.system_type,
      projects: Number(item.projects) || 0,
      active: Number(item.active) || 0,
      template: templates.get(item.system_key) || null,
    }));
  // a template whose system type has no projects any more still shows (so it can be removed)
  templates.forEach((template, key) => {
    if (!chips.some((chip) => chip.key === key)) chips.push({ key, systemType: template.system_type, projects: 0, active: 0, template });
  });
  const unassigned = (systemTypeCounts || []).find((item) => !item.system_key);
  return { total, chips, unassigned: unassigned ? Number(unassigned.projects) || 0 : 0 };
};

/* ----------------------------------------------------------------------------------------------
   History — wave 3 (30 #8 before → after · #9 patterns, project names)
---------------------------------------------------------------------------------------------- */
const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/** A before / after value as text ('' → '—'); dates and ISO date-times as '3 Feb 2026' (company zone). */
export const historyValueText = (value, { timeZone } = {}) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'number') return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
  const text = String(value);
  if (ISO_INSTANT.test(text)) return formatDateTime(text, timeZone ? { timeZone } : undefined);
  if (ISO_DAY.test(text)) return formatDate(text);
  return text;
};

/** "Theme: White / Navy / Green → Sara Light" for one value row (added / removed / secret read naturally). */
export const historyValueLine = (field = {}, options = {}) => {
  if (field.kind === 'added') return `${field.label}: added`;
  if (field.kind === 'removed') return `${field.label}: removed`;
  if (field.kind === 'secret') return `${field.label}: changed`;
  return `${field.label}: ${historyValueText(field.before, options)} → ${historyValueText(field.after, options)}`;
};

const plainNumber = (value) => Number(value || 0).toLocaleString('en-US');

/** "Sayed imported and then deleted BOQ lines 6 times on Fri 25 Sep" + detail + advice. */
export const patternText = (pattern = {}) => {
  const day = pattern.day ? formatDate(pattern.day, { weekday: true, year: 'never' }).replace(',', '') : 'one day';
  const name = pattern.user_name || 'Someone';
  const span = durationWords((pattern.minutes || 0) * 60000);
  if (pattern.kind === 'undo_churn') {
    return {
      lead: `${name} undid ${plainNumber(pattern.count)} changes on ${day}`,
      detail: span ? `within ${span}` : '',
      advice: 'Lots of undo in one day can mean a screen did something people did not expect.',
    };
  }
  const verb = pattern.kind === 'import_then_delete' ? 'imported' : 'added';
  return {
    lead: `${name} ${verb} and then deleted ${pattern.noun || 'records'} ${plainNumber(pattern.times)} times on ${day}`,
    detail: `${plainNumber(pattern.imported)} ${verb} · ${plainNumber(pattern.deleted)} deleted${span ? ` in ${span}` : ''}`,
    advice: pattern.kind === 'import_then_delete'
      ? `The ${pattern.noun === 'BOQ lines' ? 'BOQ import' : 'import'} template may need a look.`
      : 'The form may not fit how people work — worth asking them.',
  };
};

/** Items with the project's name filled in from the insights' project_names when the entry has none. */
export const withProjectNames = (items = [], projectNames = {}) => (items || []).map((item) => (
  !String(item.project_label || '').trim() && item.project_id && projectNames?.[item.project_id]
    ? { ...item, project_label: projectNames[item.project_id] }
    : item
));

/* ----------------------------------------------------------------------------------------------
   Integrations — wave 3 (every service's status with its setup · #5 OneDrive coverage · #7 sync activity)
---------------------------------------------------------------------------------------------- */
// Where each service is set up (docs/INTEGRATIONS_SETUP.md section) and where it is used in the app.
export const SERVICE_SETUP = {
  quickbooks: { doc: 'QuickBooks Online', usedIn: 'Invoicing › Sync to QuickBooks', path: '/invoicing' },
  onedrive: { doc: 'OneDrive — project folders in the Files tab', usedIn: 'Each project’s Files tab', path: '/projects' },
  email_outbound: { doc: 'Outgoing email — SMTP', usedIn: 'Reminders, digests, Shop Drawings chase emails and the daily QuickBooks summary', path: '/shop-drawings' },
  email_inbound: { doc: 'Incoming email — a shared mailbox', usedIn: 'Consultant replies on Shop Drawings', path: '/shop-drawings' },
  microsoft_sso: { doc: 'Microsoft 365 sign-in', usedIn: 'The sign-in page — “Continue with Microsoft”', path: '' },
  passkeys: { doc: 'Passkeys — Face ID / fingerprint', usedIn: 'The sign-in page and each person’s account menu', path: '' },
  ai: { doc: 'AI assistant — Google Gemini', usedIn: 'AI page, morning brief and suggestions', path: '/ai' },
  ai_extras: {
    doc: 'AI extras — the wave-3 AI features',
    usedIn: 'Dashboard brief and pinned cards, Shop Drawings › From email, Chase supplier, project Stages and Material tabs',
    path: '',
  },
};
export const SERVICE_ORDER = ['quickbooks', 'onedrive', 'email_outbound', 'email_inbound', 'microsoft_sso', 'passkeys', 'ai', 'ai_extras'];

/** Status pill of one service: On (green) · Switched off / Setup needed (amber) · Checking… / Unknown (gray). */
export const serviceStatus = (feature, { error = false, loading = false } = {}) => {
  if (!feature || feature.placeholder) return loading && !error ? { tone: 'gray', label: 'Checking…' } : { tone: 'gray', label: 'Unknown' };
  if (feature.status === 'on') return { tone: 'green', label: 'On' };
  if (feature.status === 'off') return { tone: 'amber', label: 'Switched off' };
  return { tone: 'amber', label: 'Setup needed' };
};

/** Plain text IT can act on: what to add on the server for one service (setting names only, never values). */
export const serviceSetupNote = (feature = {}, { appName = 'Sara', origin = '' } = {}) => {
  const setup = SERVICE_SETUP[feature.key] || {};
  const lines = [`${appName} — ${feature.label || feature.key}: ${feature.status === 'on' ? 'working' : 'needs server setup'}`];
  if (feature.missing?.length) lines.push(`Add these settings on the server: ${feature.missing.join(', ')}`);
  if (feature.missing_modules?.length) lines.push(`Install these Python packages: ${feature.missing_modules.join(', ')}`);
  (feature.problems || []).forEach((problem) => lines.push(`Fix: ${problem}`));
  (feature.warnings || []).forEach((warning) => lines.push(`Note: ${warning}`));
  if (feature.optional_env?.length) lines.push(`Optional settings: ${feature.optional_env.join(', ')}`);
  if (setup.doc) lines.push(`Step-by-step guide: docs/INTEGRATIONS_SETUP.md, section “${setup.doc}”`);
  if (origin) lines.push(`App address: ${origin}`);
  return lines.join('\n');
};
